/**
 * Reads a date out of the several shapes a workbook cell can hold — an Excel serial number, a Unix
 * timestamp in seconds or milliseconds, or a date string — and renders values for display.
 */

const EXCEL_SERIAL_DATE_MIN = 60;
const EXCEL_SERIAL_DATE_MAX = 2958465;
const UNIX_TIMESTAMP_SECONDS_MIN = 1000000000;
const UNIX_TIMESTAMP_SECONDS_MAX = 2000000000;
const UNIX_TIMESTAMP_MS_MAX = 9999999999999;

function parseTimestampNumber(numValue: number): Date | null {
  const timestamp =
    numValue > UNIX_TIMESTAMP_SECONDS_MIN && numValue < UNIX_TIMESTAMP_SECONDS_MAX ? numValue * 1000 : numValue;
  const date = new Date(timestamp);
  return isNaN(date.getTime()) ? null : date;
}

// Helper: Parse a value as a Date (Excel serial number, Unix timestamp, ISO string)
export function parseDateValue(val: string | number | Date): Date | null {
  if (val instanceof Date) {
    return isNaN(val.getTime()) ? null : val;
  }

  if (typeof val === "number") {
    if (!isFinite(val)) return null;

    if (val > EXCEL_SERIAL_DATE_MIN && val < EXCEL_SERIAL_DATE_MAX) {
      // Excel bug: 1900 is treated as a leap year, so use Dec 31 plus -1 for serials >= 60.
      const excelEpoch = new Date(Date.UTC(1899, 11, 31));
      let serial = val;
      if (serial >= 60) serial -= 1; // Excel leap year bug
      return new Date(excelEpoch.getTime() + serial * 86400000);
    }

    if (val > EXCEL_SERIAL_DATE_MAX && val < UNIX_TIMESTAMP_MS_MAX) {
      return parseTimestampNumber(val);
    }

    return null;
  }

  const trimmedValue = val.trim();
  const isNumericString = /^\d+(\.\d+)?$/.test(trimmedValue);
  if (isNumericString) {
    const numValue = parseFloat(trimmedValue);
    if (numValue > UNIX_TIMESTAMP_SECONDS_MIN && numValue < UNIX_TIMESTAMP_MS_MAX) {
      return parseTimestampNumber(numValue);
    }
    return null;
  }

  const d = new Date(trimmedValue);
  if (!isNaN(d.getTime())) return d;

  return null;
}

// Helper: Format a Date as MM/DD/YYYY
function formatDateMMDDYYYY(displayValue: string | number | Date): string {
  const date = parseDateValue(displayValue);
  if (!date) return "";
  const mm = String(date.getUTCMonth() + 1).padStart(2, "0");
  const dd = String(date.getUTCDate()).padStart(2, "0");
  const yyyy = date.getUTCFullYear();
  return `${mm}/${dd}/${yyyy}`;
}

export function formatDisplayValue(
  value: string | number | Date | null | undefined,
  _groupByProp?: string,
  blankLabel = "Blank"
): string {
  if (value == null || (typeof value === "string" && value.trim() === "")) {
    return "";
  }

  const stringValue = String(value);
  if (blankLabel && stringValue === blankLabel) {
    return stringValue;
  }

  const formattedDate = formatDateMMDDYYYY(value);
  if (formattedDate) {
    // Force text so Excel does not reinterpret the header/subtotal label.
    return `'${formattedDate}`;
    // return `="${formattedDate}"`;
  }

  return stringValue.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}
